import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, Table, Button, Spinner, Alert, Badge, Form, Row, Col } from 'react-bootstrap';
import type { components } from '@telemedicine/shared';
import { listCases } from '../services/api';
import { usePolling } from '../usePolling';
import { POLL_INTERVAL_MS } from '../config';
import { personName } from '../people';
import { enumLabel } from '../labels';
import { getLang } from '../language';

type CaseSummary = components['schemas']['CaseSummary'];
type CaseStatus = components['schemas']['CaseStatus'];

const priorityVariant: Record<string, string> = {
  URGENT: 'danger',
  HIGH: 'warning',
  MEDIUM: 'info',
  LOW: 'secondary',
};

// Statuses offered in the filter (all of them; the API filters server-side via a GSI).
const STATUSES: CaseStatus[] = [
  'DRAFT',
  'SUBMITTED',
  'TRIAGE',
  'ASSIGNED',
  'UNDER_REVIEW',
  'WAITING_FOR_LOCAL_DOCTOR',
  'VIDEO_REQUIRED',
  'SPECIALIST_FACE_TO_FACE',
  'RECOMMENDATION_PROVIDED',
  'LOCAL_FOLLOW_UP',
  'CLOSED',
];

const PAGE_SIZE = 25;

export function CasesList({ onOpen }: { onOpen: (caseId: string) => void }) {
  const lang = getLang();
  const [items, setItems] = useState<CaseSummary[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Server-side filter: status. Client-side quick filter: free text over loaded rows.
  const [status, setStatus] = useState<string>('');
  const [quickFilter, setQuickFilter] = useState('');
  // Whether the user has paged past the first page via "Load more" — while they have, we don't
  // auto-refresh (it would discard their loaded pages / yank the view mid-browse).
  const pagedDeeper = useRef(false);

  // Fetch the first page for the current status filter (resets the list).
  const loadFirst = useCallback(() => {
    setLoading(true);
    setError(null);
    pagedDeeper.current = false;
    listCases({ status: status || undefined, limit: PAGE_SIZE })
      .then((page) => {
        setItems(page.items);
        setNextCursor(page.nextCursor ?? null);
      })
      .catch(() => setError('Failed to load cases'))
      .finally(() => setLoading(false));
  }, [status]);

  // Quiet background refresh of the first page (no spinner) so new cases appear without a manual
  // reload. Skipped while the user has loaded further pages, so their browsing isn't disturbed.
  const refreshQuietly = useCallback(() => {
    if (pagedDeeper.current) return;
    listCases({ status: status || undefined, limit: PAGE_SIZE })
      .then((page) => {
        setItems(page.items);
        setNextCursor(page.nextCursor ?? null);
      })
      .catch(() => {
        /* transient; keep the current list */
      });
  }, [status]);

  useEffect(() => {
    loadFirst();
  }, [loadFirst]);

  // Auto-refresh the first page (visible tab only; instant on refocus) so newly created/assigned
  // cases appear without a manual reload. Skipped while the user has paged deeper.
  usePolling(refreshQuietly, POLL_INTERVAL_MS);

  const loadMore = () => {
    if (!nextCursor) return;
    setLoadingMore(true);
    setError(null);
    pagedDeeper.current = true;
    listCases({ status: status || undefined, cursor: nextCursor, limit: PAGE_SIZE })
      .then((page) => {
        setItems((prev) => [...prev, ...page.items]);
        setNextCursor(page.nextCursor ?? null);
      })
      .catch(() => setError('Failed to load more cases'))
      .finally(() => setLoadingMore(false));
  };

  // Client-side quick filter over the rows already loaded (case id or title). This narrows what's
  // on screen; it does not search the whole dataset — use the status filter (server-side) for that.
  const visible = useMemo(() => {
    const q = quickFilter.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (c) =>
        c.caseId.toLowerCase().includes(q) ||
        c.title.toLowerCase().includes(q),
    );
  }, [items, quickFilter]);

  return (
    <Card className="shadow-sm">
      <Card.Body>
        <Card.Title as="h2" className="h4 mb-3">Cases</Card.Title>

        <Row className="g-2 mb-3">
          <Col sm={5} md={4}>
            <Form.Label className="small text-muted mb-1">Status</Form.Label>
            <Form.Select
              size="sm"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              aria-label="Filter by status"
            >
              <option value="">All statuses</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {enumLabel('caseStatus', s, lang)}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col sm={7} md={5}>
            <Form.Label className="small text-muted mb-1">Filter loaded cases</Form.Label>
            <Form.Control
              size="sm"
              type="search"
              placeholder="Case ID or title…"
              value={quickFilter}
              onChange={(e) => setQuickFilter(e.target.value)}
              aria-label="Quick filter by case id or title"
            />
          </Col>
        </Row>

        {loading && (
          <div className="text-muted d-flex align-items-center">
            <Spinner animation="border" size="sm" className="me-2" /> Loading cases…
          </div>
        )}
        {error && <Alert variant="danger">{error}</Alert>}
        {!loading && !error && items.length === 0 && (
          <p className="text-muted mb-0">
            {status ? 'No cases with this status.' : 'No cases yet. Create one from “New case”.'}
          </p>
        )}
        {!loading && !error && items.length > 0 && visible.length === 0 && (
          <p className="text-muted mb-0">No loaded cases match “{quickFilter}”.</p>
        )}

        {!loading && !error && visible.length > 0 && (
          <Table hover responsive className="align-middle mb-0">
            <thead>
              <tr>
                <th>Case</th>
                <th>Title</th>
                <th>Status</th>
                <th>Priority</th>
                <th>Created by</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((c) => (
                <tr key={c.caseId}>
                  <td>
                    <Button variant="link" className="p-0" onClick={() => onOpen(c.caseId)}>
                      {c.caseId}
                    </Button>
                  </td>
                  <td>{c.title}</td>
                  <td>
                    <Badge bg="light" text="dark">{enumLabel('caseStatus', c.status, lang)}</Badge>
                  </td>
                  <td>
                    <Badge bg={priorityVariant[c.priority] ?? 'secondary'}>{enumLabel('priority', c.priority, lang)}</Badge>
                  </td>
                  <td className="text-muted small">{personName(c.createdByName, c.createdBy)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        {!loading && !error && nextCursor && (
          <div className="text-center mt-3">
            <Button variant="outline-primary" size="sm" onClick={loadMore} disabled={loadingMore}>
              {loadingMore ? (
                <><Spinner animation="border" size="sm" className="me-2" />Loading…</>
              ) : (
                'Load more'
              )}
            </Button>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}
